import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDaysIso,
  assignEmployeeColors,
  chipLabel,
  ganttBarFromMinutes,
  ganttTicks,
  intervalsByDay,
  timeToMinutes,
  todayIsoParis,
  windowSamples,
  type ActivityPresence,
  type ActivityQualification,
  type BoardActivity,
  type Employee,
  type GanttRange,
  type Interval,
  type WindowCorrection,
} from '@lemontaigu/staff';
import { useBoard, useRoster, useTimesheet } from '@lemontaigu/staff/react';
import { fmtDayLabel } from './logic.js';

// « Historique équipe » (Opérations, operator-only) — the ACTIVITY planning
// rebuilt from what actually happened. docs/PREDICTIVE-STAFFING-BRIEF.md §7.0.
//
// This is the ACTIVITY Gantt, not a punch strip: one row per activity, its bar
// placed where the activity REALLY ran (derived from the punches of the people
// attributed to it), with the planned window drawn behind it as a ghost so the
// drift is visible without arithmetic. The per-person punch view already exists
// as Planning's « Réalisé (pointages) » strip and is deliberately NOT repeated
// here — the whole point of this tab is the activity-level reconstruction that
// Planning cannot show for a past day.
//
// No forecast, no score. May–July had no real forecast to judge against (the
// operator's plannings then were estimates), so this shows only what happened;
// the planned bar is context, not a baseline being graded.
//
// Attribution precedence is actualWindows' own: the day's presence rows, else
// « activité principale », else anyone qualified. An activity whose people never
// clock (Dima) is flagged rather than dropped.

const EARLIEST = '2026-05-01';

export function Historique() {
  const board = useBoard();
  const roster = useRoster();
  const timesheet = useTimesheet();

  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [activities, setActivities] = useState<BoardActivity[]>([]);
  const [quals, setQuals] = useState<ActivityQualification[]>([]);
  const [presence, setPresence] = useState<ActivityPresence[]>([]);
  const [byDay, setByDay] = useState<Map<string, Map<string, Interval[]>>>(new Map());
  const [date, setDate] = useState(addDaysIso(todayIsoParis(), -1));
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const today = todayIsoParis();
      const [team, acts, qualifications, rows, entries] = await Promise.all([
        roster.all(),
        board.activities(),
        board.qualifications(),
        board.presence(EARLIEST, today),
        timesheet.entriesBetween(`${EARLIEST}T00:00:00Z`, `${today}T23:59:59Z`),
      ]);
      setEmployees(team);
      setActivities(acts);
      setQuals(qualifications);
      setPresence(rows);
      setByDay(
        intervalsByDay(
          entries.map((e) => ({ employeeId: e.employeeId, clockIn: e.clockIn, clockOut: e.clockOut })),
        ),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [board, roster, timesheet]);

  useEffect(() => {
    void load();
  }, [load]);

  const employeesById = useMemo(() => new Map((employees ?? []).map((e) => [e.id, e])), [employees]);
  const colors = useMemo(() => assignEmployeeColors((employees ?? []).map((e) => e.id)), [employees]);
  const daysWithData = useMemo(() => [...byDay.keys()].sort(), [byDay]);

  const timed = useMemo(() => activities.filter((a) => a.active && !a.untimed), [activities]);

  /** Realised window per activity for the chosen day — the reconstruction itself. */
  const corrections = useMemo<WindowCorrection[]>(() => {
    const intervalsByEmployee = byDay.get(date);
    if (!intervalsByEmployee || timed.length === 0) return [];
    return windowSamples({
      date,
      activities: timed.map((a) => ({
        id: a.id,
        label: a.label,
        windowStart: a.windowStart.slice(0, 5),
        windowEnd: a.windowEnd.slice(0, 5),
      })),
      qualifications: quals,
      attributed: presence
        .filter((p) => p.date === date)
        .map((p) => ({ employeeId: p.employeeId, activityId: p.activityId })),
      mains: quals.filter((q) => q.isMain).map((q) => ({ employeeId: q.employeeId, activityId: q.activityId })),
      intervalsByEmployee,
    });
  }, [byDay, date, timed, quals, presence]);

  const byActivityId = useMemo(() => new Map(corrections.map((c) => [c.activityId, c])), [corrections]);

  /** Who the reconstruction used for each activity — shown as chips, like the day board. */
  const peopleFor = (activityId: number): string[] => {
    const attributed = presence.filter((p) => p.date === date && p.activityId === activityId).map((p) => p.employeeId);
    if (attributed.length > 0) return attributed;
    const mains = quals.filter((q) => q.isMain && q.activityId === activityId).map((q) => q.employeeId);
    if (mains.length > 0) return mains;
    return quals.filter((q) => q.activityId === activityId).map((q) => q.employeeId);
  };

  // Range spans BOTH the planned and the realised bars, so a big drift is never
  // clipped off the edge of the chart — the drift is the thing being looked at.
  const range: GanttRange = useMemo(() => {
    let start = Infinity;
    let end = -Infinity;
    for (const a of timed) {
      start = Math.min(start, timeToMinutes(a.windowStart));
      end = Math.max(end, timeToMinutes(a.windowEnd));
    }
    for (const c of corrections) {
      start = Math.min(start, timeToMinutes(c.actual.start));
      end = Math.max(end, timeToMinutes(c.actual.end));
    }
    if (!Number.isFinite(start) || !Number.isFinite(end)) return { startMin: 6 * 60, endMin: 23 * 60 };
    return { startMin: Math.floor(start / 60) * 60, endMin: Math.ceil(end / 60) * 60 };
  }, [timed, corrections]);
  const ticks = useMemo(() => ganttTicks(range), [range]);

  const jump = (direction: 1 | -1) => {
    const pool = daysWithData;
    if (pool.length === 0) return;
    const idx = pool.indexOf(date);
    const next = idx === -1 ? pool[pool.length - 1] : pool[Math.min(pool.length - 1, Math.max(0, idx + direction))];
    if (next) setDate(next);
  };

  const rows = useMemo(
    () =>
      [...timed].sort((a, b) => {
        const sa = byActivityId.get(a.id)?.actual.start ?? a.windowStart.slice(0, 5);
        const sb = byActivityId.get(b.id)?.actual.start ?? b.windowStart.slice(0, 5);
        return timeToMinutes(sa) - timeToMinutes(sb);
      }),
    [timed, byActivityId],
  );

  return (
    <div className="planning">
      <div className="equipe-head">
        <h2 className="section-title">Historique équipe</h2>
        <span className="equipe-hint">
          Le planning des activités reconstruit d’après les pointages : chaque activité est placée là où elle s’est
          réellement déroulée. Le trait clair derrière montre l’horaire planifié. Faites défiler les jours pour voir un
          rythme se dessiner.
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {employees === null && <p className="staff-empty">Chargement…</p>}

      {employees !== null && (
        <div className="planning-card">
          <header className="planning-card-head">
            <button type="button" className="planning-add" onClick={() => jump(-1)} disabled={daysWithData.length === 0}>
              ← précédent
            </button>
            <input
              type="date"
              value={date}
              min={EARLIEST}
              max={todayIsoParis()}
              onChange={(e) => e.target.value && setDate(e.target.value)}
            />
            <strong>{fmtDayLabel(date)}</strong>
            <button type="button" className="planning-add" onClick={() => jump(1)} disabled={daysWithData.length === 0}>
              suivant →
            </button>
          </header>

          {timed.length === 0 ? (
            <p className="staff-empty">Aucune activité active à reconstruire.</p>
          ) : !byDay.has(date) ? (
            <p className="staff-empty">Aucun pointage ce jour-là — rien à reconstruire.</p>
          ) : (
            <div className="gantt-charts">
              <div className="gantt gantt-planning">
                <div className="gantt-row gantt-axis">
                  <div className="gantt-label" />
                  <div className="gantt-track">
                    {ticks.map((t) => (
                      <span key={t.label} className="gantt-tick" style={{ left: `${t.leftPct}%` }}>
                        {t.label}
                      </span>
                    ))}
                  </div>
                </div>

                {rows.map((activity) => {
                  const c = byActivityId.get(activity.id);
                  const planned = ganttBarFromMinutes(
                    timeToMinutes(activity.windowStart),
                    timeToMinutes(activity.windowEnd),
                    range,
                  );
                  const real = c
                    ? ganttBarFromMinutes(timeToMinutes(c.actual.start), timeToMinutes(c.actual.end), range)
                    : null;
                  const people = c ? peopleFor(activity.id) : [];
                  const shift =
                    c && (Math.abs(c.actual.startShift) >= 10 || Math.abs(c.actual.endShift) >= 10)
                      ? `${c.actual.startShift >= 0 ? '+' : '−'}${Math.abs(c.actual.startShift)} min au début`
                      : null;

                  return (
                    <div key={activity.id} className="gantt-row gantt-arow">
                      <div className="gantt-label">
                        <span className="gantt-label-text" title={activity.label}>
                          {activity.label}
                        </span>
                      </div>
                      <div className="gantt-track">
                        {ticks.map((t) => (
                          <span key={t.label} className="gantt-line" style={{ left: `${t.leftPct}%` }} />
                        ))}

                        {/* Planned, behind — context for the drift, not a baseline being graded. */}
                        <div
                          className="gantt-bar-planned"
                          style={{ left: `${planned.leftPct}%`, width: `${planned.widthPct}%` }}
                          title={`Planifié ${activity.windowStart.slice(0, 5)}–${activity.windowEnd.slice(0, 5)}`}
                        />

                        {real && c ? (
                          <div
                            className={`gantt-bar is-covered${c.trust === 'assumed' ? ' is-assumed' : ''}`}
                            style={{ left: `${real.leftPct}%`, width: `${real.widthPct}%` }}
                            title={`Réel ${c.actual.start}–${c.actual.end}${
                              c.actual.gaps.length > 0
                                ? ` · interruption ${c.actual.gaps.map((g) => `${g.start}–${g.end}`).join(', ')}`
                                : ''
                            }`}
                          >
                            <span className="gantt-bar-window">
                              {c.actual.start}–{c.actual.end}
                            </span>
                            {people.slice(0, 6).map((id) => {
                              const e = employeesById.get(id);
                              return (
                                <span
                                  key={id}
                                  className="gantt-chip"
                                  style={{ background: colors.get(id) }}
                                  title={e ? `${e.firstName} ${e.lastName}` : id}
                                >
                                  {e ? chipLabel(e) : '?'}
                                </span>
                              );
                            })}
                            {c.trust === 'assumed' && (
                              <span className="gantt-muted" title="Horaire déduit des collègues — cette personne ne pointe pas">
                                hyp.
                              </span>
                            )}
                            {shift && <span className="gantt-muted">{shift}</span>}
                          </div>
                        ) : (
                          <div
                            className="gantt-bar is-noDemand"
                            style={{ left: `${planned.leftPct}%`, width: `${planned.widthPct}%` }}
                            title="Aucun pointage rattachable à cette activité ce jour-là"
                          >
                            <span className="gantt-muted">pas de pointage</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
